/**
 * Constantes de la instrumentación GA4 + dataLayer (Guía de Medición v9).
 */

import { slugify } from './dataLayer'

/**
 * `module_type` del directorio alfabético A–Z (listado de artistas/recintos).
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica. No existe ficha (CSV) en
 * `docs/measurement/components-fingerprint/` ni valor en el enum `module_type`
 * del diccionario (`analytic-variables.md`) para un directorio alfabético: el
 * enum solo contempla listados de productos (`product_list_*`, `search_results`,
 * `hero_carousel`, `product_card`, `province_selector`…). Este valor está
 * centralizado aquí para poder sustituirlo por el valor canónico en cuanto el
 * equipo de Analítica lo confirme, sin tocar los componentes de hoja.
 */
export const ALPHA_LISTING_MODULE_TYPE = 'alpha_listing'

/**
 * Los dos valores "sin dato" de la guía. NO son intercambiables: cada
 * parámetro tiene el suyo fijado en el diccionario o en la ficha del componente
 * (p. ej. `items[].item_brand` es `not_set` en `view_item_list` y `no_aplica` en
 * `select_item`), así que se eligen por parámetro, nunca por gusto.
 */
export const NOT_SET = 'not_set'
export const NO_APLICA = 'no_aplica'

/**
 * `module_type` de un listado de productos con filtros y sidebar — el enum de
 * `components-fingerprint/Listado filtrado.csv` (`product_list_filtered_sidebar |
 * product_list_by_month | product_list_secondary`). Valor canónico, no
 * provisional; vive aquí porque lo comparten todas las PLP que montan
 * `ListingFilters` + `InfiniteVirtualizedList` (promociones, lo-mas-vendido,
 * subcategoria, agenda, search, postsale), no solo la de Promociones.
 */
export const FILTERED_LISTING_MODULE_TYPE = 'product_list_filtered_sidebar'

/**
 * `module_type` de un listado de producto SECUNDARIO de la página — la franja
 * "Eventos en otras ciudades" al final de una PLP ya filtrada por provincia
 * (`OtherProvinceEvents`). Valor canónico: es el tercer valor del enum de
 * `components-fingerprint/Listado filtrado.csv` (`product_list_filtered_sidebar
 * | product_list_by_month | product_list_secondary`), justo para el listado que
 * acompaña al principal en esa misma ficha.
 */
export const SECONDARY_LISTING_MODULE_TYPE = 'product_list_secondary'

/**
 * `module_type` del grid de resultados del buscador (`/search`) — valor
 * canónico del enum de `module_type` (`analytic-variables.md`: `hero_carousel |
 * product_list_localized | product_list_filtered_sidebar | search_results`) y
 * literal fijo de `components-fingerprint/Resultados de búsqueda.csv` (todas
 * sus filas). Distinto de `FILTERED_LISTING_MODULE_TYPE`: el buscador tiene su
 * propio valor de enum, no comparte el de las PLP con sidebar de filtros.
 */
export const SEARCH_RESULTS_MODULE_TYPE = 'search_results'

/**
 * `module_type` del carril "Vistos recientemente" (`RecentlyViewed`).
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica. No hay ficha (CSV) para este módulo
 * ni valor en el enum `module_type` del diccionario que lo cubra: los valores
 * documentados son `product_list_localized`, `product_list_filtered_sidebar`,
 * `product_list_by_month`, `product_list_secondary`, `product_list_related`,
 * `search_results`, `hero_carousel`, `product_card` y `province_selector`. Se
 * sigue la convención de nombres de la familia `product_list_*`. Centralizado
 * aquí para sustituirlo por el valor canónico sin tocar el componente.
 */
export const RECENTLY_VIEWED_MODULE_TYPE = 'product_list_recently_viewed'

/**
 * Divisa de `items[].currency` en los eventos de e-commerce.
 *
 * ⚠️ Contradicción en la propia guía, pendiente de Analítica: los tres ejemplos
 * de código de `02_Snippets&DataLayer.md` (§5, §6, §7) usan `'EUR'` en
 * mayúsculas, mientras que `Listado filtrado.csv` y `Grid Cards.csv` documentan
 * `eur` en minúsculas (regla general de normalización). Se implementa `'EUR'`:
 * GA4 exige el código ISO 4217 en mayúsculas para reconocer la divisa y
 * calcular ingresos — en minúsculas no la reconoce. Centralizado aquí para
 * poder cambiarlo en un único sitio si Analítica confirma lo contrario.
 */
export const CURRENCY = 'EUR'

/**
 * `items[].item_id` de un evento a partir de su slug de URL (`ProductApiItem.url`).
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica. Ni el diccionario ni los CSV
 * documentan la regla de construcción de `item_id`: solo dan un ejemplo
 * ilustrativo (`evt_dani_martin_toledo_2026`) que NO es reconstruible desde el
 * payload de `/data` (no hay campo de artista ni de año). Se usa `url` porque es
 * el identificador estable y único que el propio front ya emplea para navegar
 * (`resolveCardHref`, `mapProductToCard.ts`), normalizado a `_` para respetar el
 * formato del ejemplo. Centralizado aquí para sustituirlo por la regla canónica
 * sin tocar los componentes de hoja.
 */
export function buildEventItemId(url: string): string {
  return `evt_${slugify(url).replace(/-/g, '_')}`
}

/**
 * `page_title` de la PDP (`Comprar.csv`/`Ubicación Detalles Producto.csv`/
 * `Tabla Detalles Producto.csv`/`Video.csv`/`Opiniones.csv`: las cinco fichas
 * repiten el mismo ejemplo `nombre_del_evento_-_entradas`).
 */
export function buildPdpPageTitle(title: string): string {
  return `${slugify(title)}_-_entradas`
}

/**
 * `items[].price_type` de `add_to_cart`.
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica. `Comprar.csv` documenta un único
 * valor de ejemplo (`full_price`) y ni `analytic-variables.md` ni ningún otro
 * CSV listan este parámetro ni su enum completo (¿`discounted`/`promotion`
 * para precio rebajado?). Se usa `full_price` siempre, sin intentar inferir un
 * segundo valor no documentado — mismo criterio que `CURRENCY`/
 * `buildEventItemId` en este fichero.
 */
export const ADD_TO_CART_PRICE_TYPE = 'full_price'

/** `cta_position` del CTA "Comprar" (guía `02_Snippets&DataLayer.md` §7, enum
 * en comentario: `above_fold | sticky | below_fold | inline_buy_button`). */
export const CTA_POSITION_ABOVE_FOLD = 'above_fold'
export const CTA_POSITION_STICKY = 'sticky'
/** Botón «Comprar» de una card de listado (`Comprar.csv` → «directo desde tarjeta»). */
export const CTA_POSITION_INLINE_BUY_BUTTON = 'inline_buy_button'

/**
 * `item_list_id`/`item_list_name` fijos de `add_to_cart` (`Comprar.csv`): el
 * mismo CTA de compra, sin importar si se dispara desde el hero o desde la
 * barra flotante.
 */
export const PDP_BUY_CTA_ITEM_LIST_ID = 'pdp_buy_cta'
export const PDP_BUY_CTA_ITEM_LIST_NAME = 'comprar_pdp'

/**
 * `module_name`/`module_type` del `click` sobre el CTA "Comprar" (`Comprar.csv`
 * → fila "CTA 'COMPRAR' (módulo de PDP / principal)"). `module_type: 'modal'`
 * es el valor **literal** del CSV: no hay ningún modal en esta implementación
 * (el CTA navega directo al vendor), pero se respeta tal cual documenta la
 * ficha en vez de sustituirlo por un valor no confirmado — mismo criterio de
 * "no inventar" que el resto de este fichero. ⚠️ Confirmar con Analítica si es
 * un error de la propia guía.
 */
export const PDP_BUY_CTA_CLICK_MODULE_NAME = 'product_header'
export const ARTIST_PAGE_SHARE_MODULE_NAME = 'artist_header'
export const PDP_BUY_CTA_CLICK_MODULE_TYPE = 'modal'

/**
 * Vendores de checkout reconocidos (guía `01_Scope.md` §2): `platinium |
 * onebox | qwantiq | janto | koobin | nazaries`. Mapeo por substring del
 * hostname de `purchase_url`.
 *
 * ⚠️ PROVISIONAL — pendiente de Analítica/Backend. Ni el diccionario ni los
 * CSV documentan cómo derivar el vendor a partir de la URL de compra (el
 * comentario de `Comprar.csv` dice "recoger del atributo data-vendor del
 * botón", pero `PerformanceResourceItem` no expone ningún campo de vendor —
 * solo `purchase_url`). Los dos únicos patrones con evidencia real en el
 * repo son `janto.es` (CDN de imágenes, mismo proveedor) y
 * `oneboxtickets.net` (fixture de test de `mapVenuePerformanceToProduct`); el
 * resto se detecta por si el propio nombre del vendor aparece en el host, sin
 * inventar un dominio que no se ha visto en ningún payload real.
 */
const VENDOR_HOST_PATTERNS: ReadonlyArray<readonly [pattern: string, vendor: string]> = [
  ['janto', 'janto'],
  ['onebox', 'onebox'],
  ['platinium', 'platinium'],
  ['qwantiq', 'qwantiq'],
  ['koobin', 'koobin'],
  ['nazaries', 'nazaries'],
]

/**
 * Hosts EXACTOS → vendor con respaldo documental. Única evidencia en la guía:
 * `Comprar.csv` (ambas filas de `add_to_cart`) y `02_Snippets&DataLayer.md` §7
 * ejemplifican `cta_destination_host = checkoutentradas4.elcorteingles.es` con
 * `cta_destination_vendor = janto`. Es UN ejemplo, no una regla: se mapea solo
 * ese host exacto (sin comodines `checkoutentradas*`) hasta que Analítica /
 * Backend confirmen la tabla completa host → vendor.
 *
 * ⚠️ PENDIENTE: ampliar con el resto de hosts de checkout de cada vendor.
 */
const VENDOR_EXACT_HOSTS: Readonly<Record<string, string>> = {
  'checkoutentradas4.elcorteingles.es': 'janto',
}

/**
 * Deriva `cta_destination_host`/`cta_destination_vendor` de `purchase_url`
 * para el `add_to_cart` del CTA "Comprar". `vendor` cae a `NOT_SET` cuando el
 * host no coincide con ningún patrón conocido — preferible a adivinar un
 * valor del enum cerrado que podría ser incorrecto (ver comentario de
 * `VENDOR_HOST_PATTERNS`).
 */
export function resolveCtaDestination(purchaseUrl: string | null | undefined): {
  readonly host: string
  readonly vendor: string
} {
  if (!purchaseUrl) return { host: NOT_SET, vendor: NOT_SET }

  try {
    const host = new URL(purchaseUrl).hostname.toLowerCase()
    const vendor =
      VENDOR_EXACT_HOSTS[host] ??
      VENDOR_HOST_PATTERNS.find(([pattern]) => host.includes(pattern))?.[1] ??
      NOT_SET
    return { host, vendor }
  } catch {
    return { host: NOT_SET, vendor: NOT_SET }
  }
}
